import { supabase } from "@/integrations/supabase/client";
import { formatDose } from "@/lib/decimalInput";

/**
 * Lo que se deshace al quitar del cuaderno un tratamiento aplicado, dicho con
 * sus números: «Se quitará del cuaderno la aplicación del 25/09 y se devolverán
 * 0,41 kg de Thiovit Jet a Las Norias».
 *
 * Auditoría del portal del perito (A17 · T12-02, 26-09-2026): «Marcar pendiente»
 * era el botón principal de la ficha de un tratamiento hecho y, de un toque, sin
 * preguntar, borraba el parte y devolvía el producto al almacén — aunque lo
 * hubiera aplicado otra persona. Ahora es un botón secundario y pide
 * confirmación con esta frase.
 */
export interface AppliedSummary {
  /** Día del parte (AAAA-MM-DD), el primero si hay varios sectores. */
  date: string | null;
  /** Finca (la foto del parte). */
  locationName: string | null;
  /** Lo descontado, sumado por producto y unidad. */
  products: Array<{ name: string; amount: number; unit: string }>;
}

export async function getAppliedSummaryForTask(taskId: string): Promise<AppliedSummary | null> {
  const { data: logs, error } = await supabase
    .from("spray_logs")
    .select("id, date, location_name_snapshot")
    .eq("task_id", taskId);
  if (error || !logs || logs.length === 0) return null;
  const { data: entries } = await supabase
    .from("spray_log_entries")
    .select("product_name_snapshot, amount_used, unit")
    .in("spray_log_id", logs.map((l) => l.id));
  return summarizeApplied(logs, entries || []);
}

export function summarizeApplied(
  logs: ReadonlyArray<{ date: string | null; location_name_snapshot?: string | null }>,
  entries: ReadonlyArray<{ product_name_snapshot: string | null; amount_used: number | null; unit: string | null }>,
): AppliedSummary {
  const dates = logs.map((l) => l.date).filter((d): d is string => !!d).sort();
  const byKey = new Map<string, { name: string; amount: number; unit: string }>();
  for (const e of entries) {
    const name = e.product_name_snapshot || "—";
    const unit = e.unit || "";
    const key = `${name}|${unit}`;
    const cur = byKey.get(key) || { name, amount: 0, unit };
    cur.amount += Number(e.amount_used) || 0;
    byKey.set(key, cur);
  }
  return {
    date: dates[0] ?? null,
    locationName: logs.find((l) => l.location_name_snapshot)?.location_name_snapshot ?? null,
    products: [...byKey.values()].filter((p) => p.amount > 0),
  };
}

/** «0,41 kg de Thiovit Jet, 0,11 kg de Switch» (la preposición la pone i18n). */
export function describeAppliedProducts(
  summary: AppliedSummary,
  joinWord: (amountWithUnit: string, name: string) => string,
  locale?: string,
): string {
  return summary.products
    .map((p) => joinWord(`${formatDose(p.amount, locale)} ${p.unit}`.trim(), p.name))
    .join(", ");
}
